import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import ErrorMessage from '../ErrorMessage';
import { getMap } from '../../lib/mapService';
import './SearchInput.scss';
import setNewLocation from '../../store/actions/setNewLocation';

/**
 * Text input to search for locations.
 */
class SearchInput extends PureComponent {
  static propTypes = {
    isLoadingScreen: PropTypes.bool,
    updateLocation: PropTypes.func.isRequired,
  };

  static defaultProps = {
    isLoadingScreen: false,
  };

  /**
   * @param {props} props Initial properties of the instance.
   */
  constructor(props) {
    super(props);

    this.state = { value: '', isError: false };
    this.inputRef = React.createRef();
    this.searchBox = null;

    this.marker = null;

    this.onChange = this.onChange.bind(this);
    this.onKeyPress = this.onKeyPress.bind(this);
    this.onSearchFinished = this.onSearchFinished.bind(this);
    this.updateBias = this.updateBias.bind(this);
  }

  /**
   * React lifecycle method.
   */
  componentDidMount() {
    const { google } = window;
    const { current } = this.inputRef;
    const map = getMap();

    this.searchBox = new google.maps.places.SearchBox(current);
    this.searchBox.addListener('places_changed', this.onSearchFinished);
    map.addListener('bounds_changed', this.updateBias);
  }

  /**
   * Input has changed.
   * @param {Event} e Input changed event.
   */
  onChange(e) {
    const { value } = e.target;
    this.setState({ value, isError: false });

    if (!value && this.marker) {
      this.marker.setMap(null);
    }
  }

  /**
   * Handles unfocusing input on enter.
   * @param {Event} e Input changed event.
   */
  onKeyPress(e) {
    const { key, keyCode } = e;

    if (key === 'Enter' || keyCode === 13) {
      this.inputRef.current.blur();
    }
  }

  /**
   * On user selects a prediction and retrieve more details.
   */
  onSearchFinished() {
    const { updateLocation } = this.props;
    const places = this.searchBox.getPlaces();

    if (!places.length) {
      this.setState({ isError: true });
      return;
    }

    let isFound = false;

    for (let i = 0; i < places.length; i += 1) {
      const { geometry } = places[i];

      if (geometry) {
        const coordinate = geometry.coordinate || geometry.location.toJSON();

        if (coordinate) {
          const { value } = this.state;
          const map = getMap();
          this.setMarker(coordinate, value || '');

          const bounds = new window.google.maps.LatLngBounds();

          if (geometry.viewport) {
            bounds.union(geometry.viewport);
          } else {
            bounds.extend(geometry.location);
          }

          map.fitBounds(bounds);
          updateLocation(coordinate, map.getZoom());

          isFound = true;
          break;
        }
      }
    }

    if (!isFound) {
      this.setState({ isError: true });
    }

    this.setState({ value: '', isError: false });
  }

  /**
   * Set marker on the map.
   * @param {object} coordinate lat and lng of location.
   * @param {string} title The text on the marker.
   */
  setMarker(coordinate, title) {
    if (this.marker) {
      this.marker.setMap(null);
    }

    const { google } = window;
    this.marker = new google.maps.Marker({
      position: coordinate,
      map: getMap(),
      title,
    });
  }

  /**
   * Bias the search box towards currently visible area.
   */
  updateBias() {
    this.searchBox.setBounds(getMap().getBounds());
  }

  /**
   * React lifecycle method.
   * @return {jsx} JSX.
   */
  render() {
    const { value, isError } = this.state;
    const { isLoadingScreen } = this.props;

    const error = isError ? 'error' : '';
    const loading = isLoadingScreen ? 'loading' : '';
    const className = `controls ${loading} ${error}`;

    const errorMessage = error
      ? <ErrorMessage>Not found.</ErrorMessage>
      : null;

    return (
      <>
        <input
          aria-label="Search input"
          className={className}
          id="pac-input"
          onChange={this.onChange}
          onKeyPress={this.onKeyPress}
          placeholder="Find a location..."
          ref={this.inputRef}
          type="text"
          value={value}
        />

        {errorMessage}
      </>
    );
  }
}

export default connect(
    null,

    (dispatch) => ({
      updateLocation: (coordinate, zoom) => dispatch(
          setNewLocation(coordinate, zoom),
      ),
    }),
)(SearchInput);
